import { router } from "@inertiajs/react";

import type { Column, PagyMeta } from "@/components/DataTable";
import DataTable from "@/components/DataTable";
import FilterBar, { type FilterField } from "@/components/FilterBar";
import { usePermissions } from "@/lib/permissions";
import { asistencias, exportExcelAsistencias } from "@/routes";

type AsistenciaRow = {
  user_id: number;
  full_name: string;
  rut: string;
  user_category: string | null;
  center_name: string | null;
  retiro_inscripciones: string;
  convivencia_inscripciones: string;
  retiro_asistencia: string;
  convivencia_asistencia: string;
};

const USER_CATEGORY_LABELS: Record<string, string> = {
  senora: "Señora",
  universitaria: "Universitaria",
  numeraria_auxiliar: "Numeraria Auxiliar",
  agregada: "Agregada",
  numeraria: "Numeraria",
  la_cuesta: "S V",
  la_cuesta_sn: "S SV",
  la_cuesta_cp: "CP SV",
};

type Props = {
  data: AsistenciaRow[];
  pagy: PagyMeta;
  filters: Record<string, string>;
  centers: { id: number; name: string }[];
  years: number[];
};

function exportUrl(path: string, filters: Record<string, string>): string {
  const qs = new URLSearchParams(Object.entries(filters)).toString();
  return qs ? `${path}?${qs}` : path;
}

const COLUMNS: Column<AsistenciaRow>[] = [
  { header: "Nombre", cell: (r) => r.full_name },
  { header: "RUT", cell: (r) => r.rut },
  {
    header: "Categoría",
    cell: (r) =>
      r.user_category
        ? (USER_CATEGORY_LABELS[r.user_category] ?? r.user_category)
        : "-",
  },
  { header: "Centro", cell: (r) => r.center_name || "-" },
  {
    header: "Inscripciones Retiros",
    cell: (r) => r.retiro_inscripciones || "-",
  },
  {
    header: "Inscripciones Convivencias",
    cell: (r) => r.convivencia_inscripciones || "-",
  },
  {
    header: "Asistencia Retiros",
    cell: (r) => r.retiro_asistencia || "-",
  },
  {
    header: "Asistencia Convivencias",
    cell: (r) => r.convivencia_asistencia || "-",
  },
];

export default function AsistenciasIndex({
  data: rows,
  pagy,
  filters,
  centers,
  years,
}: Props) {
  const { isAdmin } = usePermissions();

  const filterFields: FilterField[] = [
    {
      key: "center_id",
      label: "Centro",
      type: "select",
      disabled: !isAdmin,
      options: isAdmin
        ? [
            { label: "Todos los centros", value: "" },
            ...centers.map((c) => ({ label: c.name, value: String(c.id) })),
          ]
        : centers.map((c) => ({ label: c.name, value: String(c.id) })),
    },
    {
      key: "year",
      label: "Año",
      type: "select",
      options: years.map((y) => ({ label: String(y), value: String(y) })),
    },
    {
      key: "name",
      label: "Nombre",
      type: "text",
      placeholder: "Buscar por nombre...",
    },
    {
      key: "user_category",
      label: "Categoría",
      type: "select",
      options: [
        { label: "Todas", value: "" },
        ...Object.entries(USER_CATEGORY_LABELS).map(([value, label]) => ({
          value,
          label,
        })),
      ],
    },
  ];

  function navigate(params: Record<string, string | number>) {
    router.get(asistencias.index().url, params, {
      preserveScroll: true,
      replace: true,
    });
  }

  const emptyMessage = filters.center_id
    ? "No hay usuarios registrados en este centro."
    : "No hay usuarios registrados.";

  return (
    <div className="w-full py-10">
      <div className="px-6 sm:px-8 lg:px-12">
        <h1 className="mb-6 font-sans text-3xl font-semibold text-ink">
          Reporte
        </h1>

        <FilterBar
          fields={filterFields}
          values={filters}
          onChange={(newFilters) => navigate({ ...newFilters, page: 1 })}
        />
      </div>

      <DataTable
        columns={COLUMNS}
        rows={rows}
        keyExtractor={(r) => r.user_id}
        utilities={
          <a
            href={exportUrl(exportExcelAsistencias().url, filters)}
            className="font-sans text-sm font-light text-ink underline underline-offset-2 hover:text-steel"
          >
            Exportar Excel
          </a>
        }
        pagy={pagy}
        onPageChange={(page) => navigate({ ...filters, page })}
        emptyMessage={emptyMessage}
      />
    </div>
  );
}
